import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { PROGRAM_KINDS, type Program, type ProgramKind, type ProgramsResponse } from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { programKindLabel } from "../lib/labels";
import { useCan, useOrg } from "../lib/org";
import { useMe } from "../lib/session";

export function ProgramsPage() {
  const { data: me } = useMe();
  const can = useCan();
  const [editing, setEditing] = useState<Program | "new" | null>(null);
  const programs = useQuery({
    queryKey: ["programs", me?.activeRole],
    queryFn: () => api<ProgramsResponse>("/v1/programs"),
  });
  return (
    <>
      <PageTitle
        title="البرامج"
        sub="لكل برنامج سياساته الخاصة (المقادير، والمراجعة، والتقييم) — تُضبط من صفحة «سياسات البرامج»."
        actions={
          can.has("programs.manage") && (
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <Plus size={18} strokeWidth={1.75} /> برنامج جديد
            </button>
          )
        }
      />
      <section className="card">
        {programs.isPending ? (
          <Loading />
        ) : programs.isError ? (
          <Empty title="تعذر التحميل">{errorText(programs.error)}</Empty>
        ) : programs.data.programs.length === 0 ? (
          <Empty title="لا توجد برامج بعد">
            البرامج الأولى المعتمدة: الحفظ الجديد، والمراجعة والتثبيت، والحفظ مع المراجعة، والإجازة.
          </Empty>
        ) : (
          <ul className="list">
            {programs.data.programs.map((p) => (
              <li key={p.id}>
                <span>
                  <b>{p.name}</b> <Badge tone="brand">{programKindLabel[p.kind]}</Badge>{" "}
                  {p.archivedAt && <Badge>مؤرشف</Badge>}
                </span>
                {can.has("programs.manage") && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(p)}>
                    تعديل
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <ProgramForm program={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function ProgramForm({ program, onClose }: { program: Program | "new" | null; onClose: () => void }) {
  const qc = useQueryClient();
  const org = useOrg();
  const { busy, error, run } = useSubmit();
  const current = program && program !== "new" ? program : null;
  const [form, setForm] = useState<{ name: string; kind: ProgramKind }>({ name: "", kind: "memorization" });
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const key = current?.id ?? (program === "new" ? "new" : null);
  if (key !== loadedFor) {
    setLoadedFor(key);
    setForm({ name: current?.name ?? "", kind: current?.kind ?? "memorization" });
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      if (current) await send("PATCH", `/v1/programs/${current.id}`, form);
      else await send("POST", "/v1/programs", { ...form, organizationId: org!.id });
      await qc.invalidateQueries({ queryKey: ["programs"] });
      onClose();
    }, errorText);
  }
  async function toggleArchive() {
    if (!current) return;
    await run(async () => {
      await send("PATCH", `/v1/programs/${current.id}`, { archived: !current.archivedAt });
      await qc.invalidateQueries({ queryKey: ["programs"] });
      onClose();
    }, errorText);
  }
  return (
    <Modal title={current ? "تعديل البرنامج" : "برنامج جديد"} open={program !== null} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field id="p-name" label="اسم البرنامج">
          <input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field id="p-kind" label="النوع" hint="قواعد التعاهد الخاصة لا تُطبق إلا على برامج من نوع «التعاهد»">
          <select
            id="p-kind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value as ProgramKind })}
          >
            {PROGRAM_KINDS.map((k) => (
              <option key={k} value={k}>
                {programKindLabel[k]}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || form.name.trim().length < 2}>
            {busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
          {current && (
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy}
              onClick={() => void toggleArchive()}
            >
              {current.archivedAt ? "إلغاء الأرشفة" : "أرشفة"}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
